import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Transition } from 'react-transition-group'
import Typography from '@material-ui/core/Typography'
import Avatar from '@material-ui/core/Avatar'
import IconButton from '@material-ui/core/IconButton'
import Fade from '@material-ui/core/Fade'
import CloseIcon from '@material-ui/icons/Close'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import CardMembershipOutlinedIcon from '@material-ui/icons/CardMembershipOutlined'
import ButtonCta from '@components/atoms/button-cta'
import { coachQualifiesForPgaMemberTag } from '@lib/pga-coach/coach-credentials'
import defaults from '@config/defaults'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import { makeStyles } from '@material-ui/core/styles'
import MarkdownText from '@components/pga-coach-offering-card/markdown-text'

const toTitleFormat = (value = '') => value
  .split(/[\s_-]+/)
  .filter(Boolean)
  .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
  .join(' ')

const getCoachFirstName = (name = '') => {
  const trimmed = name.trim()
  if (!trimmed) return 'Coach'
  return trimmed.split(/\s+/)[0]
}

const PANEL_SLIDE_MS = 300
const BACKDROP_FADE_MS = 220

const panelSlideTransition = {
  transition: `transform ${PANEL_SLIDE_MS}ms cubic-bezier(0, 0, 0.2, 1)`,
  transform: 'translateX(100%)',
}

const panelSlideStates = {
  entering: { transform: 'translateX(0)' },
  entered: { transform: 'translateX(0)' },
  exiting: { transform: 'translateX(100%)' },
  exited: { transform: 'translateX(100%)' },
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

const useStyles = makeStyles((theme) => ({
  rootSidebar: ({ panelTop = 0 }) => ({
    position: 'fixed',
    top: panelTop,
    right: 0,
    height: `calc(100vh - ${panelTop}px)`,
    width: 400,
    maxWidth: 400,
    zIndex: theme.zIndex.modal,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.common.white,
    boxShadow: '-8px 0 24px rgba(15, 34, 52, 0.12)',
    boxSizing: 'border-box',
    outline: 'none',
  }),
  rootFullscreen: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.modal,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.common.white,
    boxSizing: 'border-box',
    outline: 'none',
  },
  backdrop: ({ panelTop = 0 }) => ({
    position: 'fixed',
    top: panelTop,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.modal - 1,
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    backgroundColor: 'rgba(15, 34, 52, 0.45)',
    outline: 'none',
  }),
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    flexShrink: 0,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(0, 3, 2),
  },
  profileBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: theme.spacing(3),
  },
  avatar: {
    width: 120,
    height: 120,
    marginBottom: theme.spacing(1.5),
  },
  name: {
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: '#6d7784',
  },
  sectionHeading: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    marginBottom: theme.spacing(1),
  },
  bio: {
    color: theme.palette.text.primary,
    '& p': {
      margin: 0,
      marginBottom: theme.spacing(1),
    },
    '& p:last-child': {
      marginBottom: 0,
    },
  },
  highlightsSection: {
    marginTop: theme.spacing(3),
  },
  highlightList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  highlightItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  highlightIcon: {
    color: theme.palette.blue.main,
    fontSize: theme.typography.pxToRem(18),
    flexShrink: 0,
  },
  highlightText: {
    color: '#6d7784',
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1.4,
  },
  footer: {
    flexShrink: 0,
    padding: theme.spacing(2, 3, 3),
    borderTop: '1px solid #dfe3ea',
  },
  ctaButton: {
    width: '100%',
    borderRadius: 999,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(15),
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
  },
}))

function PgaCoachInfoPanel({
  coach,
  locationLabel,
  profileUrl,
  onClose,
  onCtaClick,
  layout,
  panelTop = 0,
}) {
  const classes = useStyles({ panelTop })
  const panelRef = useRef(null)
  const restoreFocusRef = useRef(null)
  const historyPushedRef = useRef(false)
  const popstateHandlerRef = useRef(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const dropPushedHistoryEntry = () => {
    if (!historyPushedRef.current) return
    historyPushedRef.current = false
    if (popstateHandlerRef.current) {
      window.removeEventListener('popstate', popstateHandlerRef.current)
      popstateHandlerRef.current = null
    }
    window.history.back()
  }
  const coachName = coach?.name || 'PGA Coach'
  const coachNameId = 'pga-coach-info-panel-name'
  const subtitle = coach?.coachTitle || (coach?.credentials ? toTitleFormat(coach.credentials) : null)
  const avatarSrc = coach?.photoUrl
    ? composeOptimizedImageUrl(coach.photoUrl, 240)
    : defaults.coachPlaceholder
  const resolvedProfileUrl = profileUrl || (coach?.slug ? `/coach/${coach.slug}` : null)
  const ctaLabel = `View All Programs by ${getCoachFirstName(coach?.name)}`
  const isFullscreen = layout === 'fullscreen'
  const [isEntered, setIsEntered] = useState(false)
  const highlights = [
    ...(coachQualifiesForPgaMemberTag(coach?.memberType)
      ? [{ id: 'pga-certified', label: 'PGA Certified Professional', Icon: CardMembershipOutlinedIcon }]
      : []),
    ...(locationLabel
      ? [{ id: 'location', label: locationLabel, Icon: RoomOutlinedIcon }]
      : []),
  ]

  useEffect(() => {
    restoreFocusRef.current = document.activeElement
    const frame = requestAnimationFrame(() => setIsEntered(true))
    return () => {
      cancelAnimationFrame(frame)
      const restoreTarget = restoreFocusRef.current
      if (restoreTarget && typeof restoreTarget.focus === 'function') {
        restoreTarget.focus()
      }
    }
  }, [])

  useEffect(() => {
    if (!isEntered) return undefined
    const dialog = panelRef.current
    if (!dialog) return undefined

    const getFocusableNodes = () => Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((node) => node instanceof HTMLElement)

    const handleTab = (event) => {
      if (event.key !== 'Tab') return
      const nodes = getFocusableNodes()
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [isEntered])

  const handleClose = () => {
    if (historyPushedRef.current) {
      window.history.back()
      return
    }
    onCloseRef.current()
  }

  useEffect(() => {
    window.history.pushState({ coachInfoPanel: true }, '')
    historyPushedRef.current = true
    const handler = () => {
      historyPushedRef.current = false
      window.removeEventListener('popstate', handler)
      popstateHandlerRef.current = null
      onCloseRef.current()
    }
    popstateHandlerRef.current = handler
    window.addEventListener('popstate', handler)
    return () => {
      if (popstateHandlerRef.current) {
        window.removeEventListener('popstate', popstateHandlerRef.current)
        popstateHandlerRef.current = null
      }
      if (historyPushedRef.current) {
        historyPushedRef.current = false
        window.history.back()
      }
    }
  }, [])

  useEffect(() => {
    const handler = (event) => {
      if (event.key !== 'Escape' && event.keyCode !== 27) return
      if (historyPushedRef.current) {
        window.history.back()
        return
      }
      onCloseRef.current()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  return (
    <>
      <Fade in={isEntered} timeout={BACKDROP_FADE_MS}>
        <button
          type="button"
          className={classes.backdrop}
          aria-label="Close coach info"
          onClick={handleClose}
        />
      </Fade>
      <Transition in={isEntered} timeout={PANEL_SLIDE_MS} mountOnEnter unmountOnExit>
        {(state) => (
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={coachNameId}
            className={isFullscreen ? classes.rootFullscreen : classes.rootSidebar}
            tabIndex={-1}
            style={{
              ...panelSlideTransition,
              ...panelSlideStates[state],
            }}
          >
            <div className={classes.header}>
              <IconButton
                aria-label="Close coach info"
                onClick={handleClose}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </div>

            <div className={classes.scrollArea}>
              <div className={classes.profileBlock}>
                <Avatar
                  className={classes.avatar}
                  src={avatarSrc}
                  alt={coachName}
                />
                <Typography id={coachNameId} variant="h5" className={classes.name}>
                  {coachName}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" className={classes.subtitle}>
                    {subtitle}
                  </Typography>
                )}
              </div>

              {coach?.bio && (
                <div>
                  <Typography component="h3" className={classes.sectionHeading}>
                    About
                  </Typography>
                  <div className={classes.bio}>
                    <MarkdownText paragraphVariant="body1">
                      {coach.bio}
                    </MarkdownText>
                  </div>
                </div>
              )}

              {highlights.length > 0 && (
                <div className={classes.highlightsSection}>
                  <Typography component="h3" className={classes.sectionHeading}>
                    Highlights
                  </Typography>
                  <ul className={classes.highlightList}>
                    {highlights.map(({ id, label, Icon }) => (
                      <li key={id} className={classes.highlightItem}>
                        <Icon className={classes.highlightIcon} aria-hidden />
                        <Typography component="span" className={classes.highlightText}>
                          {label}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {resolvedProfileUrl && (
              <div className={classes.footer}>
                <ButtonCta
                  href={resolvedProfileUrl}
                  color="primary"
                  variant="contained"
                  className={classes.ctaButton}
                  onClick={() => {
                    dropPushedHistoryEntry()
                    onCtaClick?.()
                  }}
                >
                  {ctaLabel}
                </ButtonCta>
              </div>
            )}
          </div>
        )}
      </Transition>
    </>
  )
}

PgaCoachInfoPanel.propTypes = {
  coach: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    bio: PropTypes.string,
    coachTitle: PropTypes.string,
    credentials: PropTypes.string,
    memberType: PropTypes.string,
    photoUrl: PropTypes.string,
  }).isRequired,
  locationLabel: PropTypes.string,
  profileUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onCtaClick: PropTypes.func,
  layout: PropTypes.oneOf(['sidebar', 'fullscreen']),
  panelTop: PropTypes.number,
}

PgaCoachInfoPanel.defaultProps = {
  locationLabel: undefined,
  profileUrl: undefined,
  onCtaClick: undefined,
  layout: 'sidebar',
  panelTop: 0,
}

export default PgaCoachInfoPanel
